<template>
  <div class="gallery-container">
    <slot name="gallery-name">
      <h2 class="gallery-name">{{ gallery.name }}</h2>
    </slot>

    <div v-if="loading" class="loader">
      <slot name="loader">
        <span>Loading...</span>
      </slot>
    </div>

    <div v-else-if="!mediaList || mediaList.length === 0" class="no-media">
      <slot name="no-media">
        <p>No media available</p>
      </slot>
    </div>

    <template v-else>
      <slot name="media-container" :media="mediaList">
        <!-- Default media container implementation -->
      </slot>

      <slot v-if="showPagination" name="pagination">
        <div class="d-flex justify-content-center mt-4">
          <b-pagination
              v-model="currentPage"
              :total-rows="totalPages * paginationSize"
              :per-page="paginationSize"
              :limit="5"
              align="center"
              size="md"
              class="mb-0"
              @change="changePage($event)"
          >
            <template #first-text>
              <Icon name="chevronLeftDouble" width="16"/>
            </template>
            <template #prev-text>
              <Icon name="chevronLeft" width="16"/>
            </template>
            <template #next-text>
              <Icon name="chevronRight" width="16"/>
            </template>
            <template #last-text>
              <Icon name="chevronRightDouble" width="16"/>
            </template>
          </b-pagination>
        </div>
      </slot>

      <slot v-if="showInfiniteScroll" name="infinite-scroll">
        <div v-if="currentPage < totalPages">
          <div class="d-flex justify-content-center mt-3 pb-3">
            <b-spinner variant="primary" label="Spinning" v-if="loadingMore"/>
            <b-button @click="loadMore" variant="primary" size="sm" v-else
                      :disabled="currentPage >= totalPages || loadingMore">
              {{ $t('pages.checkins_detail.load_more') }}
              <Icon name="plus" width="16" vertical-align="sub"/>
            </b-button>
          </div>
        </div>
      </slot>


    </template>
  </div>
</template>

<script>
import {mapGetters, mapActions} from 'vuex'
import MessageManager from '@/domain/core/utils/messageManager'
import mqttTopics from '@/domain/core/constants/mqttTopics'
import eventBusConstants from '@/domain/core/constants/eventBus'
import {GALLERY_TAB_TYPE} from "@/domain/galleryTree/constants/galleryTabType";
import {favoritesEligibility} from "@/domain/galleryTree/constants/favoritesEligibility";
import {ctaPosition} from "@/domain/galleries/constants";
import {orderTypes} from "@/domain/cart/constants";

export default {
  name: 'BaseDisplayType',

  props: {
    gallery: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      loading: false,
      loadingMore: false,
      activeTab: null,
      mqttSubscriptions: []
    }
  },

  computed: {
    GALLERY_TAB_TYPE() {
      return GALLERY_TAB_TYPE
    },
    ...mapGetters('galleryTree', [
      'checkinId',
      'rootGalleryId',
      'priceList',
      'media',
      'priceListStructure'
    ]),
    ...mapGetters('cartDigitalItems', ['getDigitalItem']),
    ...mapGetters('checkinDetail', ['checkin', 'eventName', 'currency', 'showPrices']),

    favoriteEnabled() {
      if (!this.gallery || !this.gallery.favorites_eligibility) {
        return false
      }
      switch (this.gallery.favorites_eligibility) {
        case favoritesEligibility.NO_FAVORITES:
          return false
        case favoritesEligibility.ALL_MEDIA_ELIGIBLE:
          return true
        case favoritesEligibility.ONLY_PAID_FAVORITES:
          return this.activeTab === GALLERY_TAB_TYPE.PURCHASED
        case favoritesEligibility.ONLY_UNPAID_FAVORITES:
          return this.activeTab === GALLERY_TAB_TYPE.UNPURCHASED
        default:
          return false
      }
    },


    mediaData() {
      const galleryId = this.gallery.id
      // When displayType is 'compact', load media from all available tabs
      if (this.isCompactView) {
        return this.media(galleryId, null) // null means all tabs
      }
      return this.media(galleryId, this.activeTab)
    },
    isCompactView() {
      return ['compact', 'compact-horizontal'].includes(this.displayType)
    },
    mediaList() {
      return (this.mediaData && this.mediaData.data) || []
    },

    currentTabData() {
      // Deprecated: use mediaList instead
      return this.mediaList
    },

    currentPage() {
      return (this.mediaData && this.mediaData.currentPage) || 1
    },

    totalPages() {
      // Use the count from the current active tab instead of gallery.num_media
      if (!this.gallery || !this.activeTab || !this.gallery.tabs) {
        return 1
      }

      const currentTab = this.gallery.tabs.find(tab => tab.type === this.activeTab)
      if (!currentTab || !currentTab.count) {
        return 1
      }

      return Math.ceil(currentTab.count / this.getPaginationSize())
    },

    hasPagination() {
      return this.totalPages > 1
    },

    displayType() {
      // To be overridden by child components
      return 'base'
    },

    paginationType() {
      // To be overridden by child components
      // Returns: 'none', 'pagination', 'infinite'
      return 'pagination'
    },

    showPagination() {
      return this.hasPagination && this.paginationType === 'pagination'
    },

    showInfiniteScroll() {
      return this.hasPagination && this.paginationType === 'infinite'
    },

    availableTabs() {
      // Now gallery.tabs is an array of objects with {name, count}
      // Include tabs with count > 0 OR the currently active tab (even if count = 0)
      return (this.gallery.tabs.filter(tab => tab.count > 0 || tab.type === this.activeTab) || []).map(tab => tab.type)
    },

    hasTabSupport() {
      // To be overridden by child components
      return false
    },

    shouldShowTabs() {
      return this.hasTabSupport && this.availableTabs.length > 1
    },

    digitalItem() {
      return this.getDigitalItem(this.gallery.id, this.checkin.id)
    },
    digitalItemId() {
      return this.digitalItem ? this.digitalItem.id : null
    },
    activeTabCount() {
      if (!this.gallery || !this.activeTab) return 0
      return this.gallery.tabs.find(tab => tab.type === this.activeTab).count
    },
    modalMediasGalleryId() {
      return `modalMediasGallery-${this.gallery.type}-${this.gallery.id}`
    },
    ctaPosition() {
      return ctaPosition
    },

    priceListImg() {
      return this.gallery.price_list && this.gallery.price_list.price_list_img ? this.gallery.price_list.price_list_img : null
    },

    galleryPriceList() {
      if (!this.gallery.price_list) return false
      return this.priceListStructure({items: this.gallery.price_list.items});
    },
    packagePriceList() {
      if (!this.galleryPriceList) return false
      return this.galleryPriceList.find(price => price.last)
    },
    showPriceListCta() {
      if (!this.gallery.price_list) return false
      if (this.packagePriceList && this.packagePriceList.price <= 0) return false
      if (this.activeTab !== GALLERY_TAB_TYPE.UNPURCHASED) return false
      return this.gallery.price_list.show_cta
    },

    hasPriceList() {
      if (this.activeTab !== GALLERY_TAB_TYPE.UNPURCHASED) return false
      if (!this.showPrices) return false
      return this.gallery.price_list && this.gallery.price_list.items && this.gallery.price_list.items.length > 0
    },
    packageCustom() {
      return this.gallery && this.gallery.price_list && this.gallery.price_list.custom_cta
    },
    packageCustomPosition() {
      return this.gallery && this.gallery.price_list && this.gallery.price_list.cta_position
    },
    showPackageCustom() {
      return this.packageCustom && this.activeTab === GALLERY_TAB_TYPE.UNPURCHASED
    },
    packageCustomInCart() {
      return this.digitalItem && this.digitalItem.order_type.includes(orderTypes.PHOTOPACKAGE)
    },
    modalPriceListId() {
      return "modalPriceList-" + this.gallery.id
    },

  },

  methods: {
    ...mapActions('galleryTree', [
      'fetchMedia',
      'fetchPriceList',
      'addToFavourites',
      'removeFromFavourites',
      'checkNewMedia'
    ]),
    ...mapActions('cartDigitalItems', ['fetchDigitalItems']),

    async changeTab(tab) {
      if (this.activeTab === tab) return

      this.activeTab = tab
      this.$emit('tab-changed', tab)

      if (tab === GALLERY_TAB_TYPE.UNPURCHASED)
        await this.fetchDigitalItems()

      // Load data for the new tab
      await this.loadMedia(1)
    },

    // loadTabData renamed to loadMedia for clarity
    async loadMedia(page = 1, append = false) {

      append ? this.loadingMore = true : this.loading = true

      try {
        // When displayType is 'compact', pass null for tab to load media from all available tabs
        // Otherwise, use activeTab like before
        const tab = this.isCompactView ? null : this.activeTab
        
        await this.fetchMedia({
          galleryId: this.gallery.id,
          tab,
          page,
          append,
          paginationType: this.paginationType,
          params: {
            ...this.getMediaParams(),
            display_type: this.displayType
          }
        })
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },

    getPaginationSize() {
      // Override in child components to specify page size
      return 10 // default page size
    },

    getMediaParams() {
      // Override in child components to provide custom params
      return {
        page_size: this.getPaginationSize()
      }
    },

    openGallery(gallery) {
      this.$emit('openGallery', gallery)
    },

    openMedia(index) {
      this.$refs[this.modalMediasGalleryId].show(index)
    },

    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.loadMedia(page, false) // Replace data for regular pagination
      }
    },

    loadMore() {
      // Method for infinite scroll - appends data
      const nextPage = this.currentPage + 1
      if (nextPage <= this.totalPages) {
        this.loadMedia(nextPage, true) // Append data for infinite scroll
      }
    },

    showPriceList() {
      this.$bvModal.show(this.modalPriceListId)
    },

    async handleAddToFavourites(mediaId) {
      await this.addToFavourites({mediaId, galleryId: this.gallery.id})
      this.$emit('favourite-added', mediaId)
    },

    async handleRemoveFromFavourites(mediaId) {
      await this.removeFromFavourites({mediaId, galleryId: this.gallery.id})
      this.$emit('favourite-removed', mediaId)
    },


    // MQTT Configuration Hooks - Override in child components
    shouldListenToMqtt() {
      // Hook: Should this display type listen to MQTT notifications?
      // Override in child components to disable/enable MQTT listening
      // Return false if gallery.refresh_time is 0 or undefined
      if (!this.gallery || !this.gallery.refresh_time || this.gallery.refresh_time === 0) {
        return false
      }
      return true
    },

    getMqttTopics() {
      // Hook: Which MQTT topics should this display type listen to?
      // Override in child components to customize topics
      const topics = []

      // Always listen to gallery-specific new media
      if (this.gallery && this.gallery.id) {
        topics.push(mqttTopics.galleryTree.new_public_media)
      }

      return topics
    },

    getPicacodeTopic() {
      // Hook: Get picacode topic for broader gallery updates
      // This listens for changes that might reveal new subgalleries
      if (this.$route && this.$route.params && this.$route.params.picacode) {
        return mqttTopics.galleryTree.picacode_update
      }
      return null
    },

    async handleMqttNotification(data, topic) {
      // Hook: How should this display type handle MQTT notifications?
      // Override in child components for custom behavior
      // Default: Schedule notification via event bus

      console.log(`MQTT notification received in ${this.displayType}:`, data, 'topic:', topic)

      try {
        const refreshTime = this.gallery.refresh_time || 30 // Default 30 seconds

        // Emit MQTT message via event bus to RealtimeNotificationManager
        await this.emitEvent(eventBusConstants.realtimeNotifications.mqtt_message_received, {
          galleryId: this.gallery.id,
          refreshTime,
          notificationData: {
            type: 'gallery_update',
            galleryId: this.gallery.id,
            galleryName: this.gallery.name,
            data: data,
            topic: topic,
            displayType: this.displayType
          }
        })

      } catch (error) {
        console.error('Error handling MQTT notification:', error)
      }
    },

    async handlePicacodeNotification(data) {
      // Hook: Handle broader picacode updates that might affect gallery structure
      // Override in child components for custom behavior
      // Default: Schedule notification via event bus

      console.log(`Picacode notification received in ${this.displayType}:`, data)

      try {
        const refreshTime = this.gallery.refresh_time || 30

        // Emit MQTT message via event bus to RealtimeNotificationManager
        await this.emitEvent(eventBusConstants.realtimeNotifications.mqtt_message_received, {
          galleryId: this.gallery.id,
          refreshTime,
          notificationData: {
            type: 'picacode_update',
            galleryId: this.gallery.id,
            galleryName: this.gallery.name,
            data: data,
            picacode: this.$route.params.picacode,
            displayType: this.displayType
          }
        })

      } catch (error) {
        console.error('Error handling picacode notification:', error)
      }
    },

    // Internal MQTT setup methods
    setupMqttSubscriptions() {
      if (!this.shouldListenToMqtt()) {
        return
      }

      // Subscribe to gallery-specific topics
      const topics = this.getMqttTopics()
      topics.forEach(topic => {
        const subscriptionKey = `display-${this.displayType}-${this.gallery.id}-${Date.now()}`
        MessageManager.on(topic, (data) => {
          this.handleMqttNotification(data, topic)
        }, subscriptionKey, {gallery_id: this.gallery.id})

        // Store subscription key for cleanup
        if (!this.mqttSubscriptions) {
          this.mqttSubscriptions = []
        }
        this.mqttSubscriptions.push(subscriptionKey)
      })

      // Subscribe to picacode topic if available
      const picacodeTopic = this.getPicacodeTopic()
      if (picacodeTopic) {
        const picacodeKey = `display-picacode-${this.gallery.id}-${Date.now()}`
        MessageManager.on(picacodeTopic, (data) => {
          this.handlePicacodeNotification(data)
        }, picacodeKey, {picacode: this.$route.params.picacode})

        if (!this.mqttSubscriptions) {
          this.mqttSubscriptions = []
        }
        this.mqttSubscriptions.push(picacodeKey)
      }
    },

    cleanupMqttSubscriptions() {
      if (this.mqttSubscriptions) {
        this.mqttSubscriptions.forEach(key => {
          MessageManager.off(key)
        })
        this.mqttSubscriptions = []
      }
    },

    // Hook: Handle notification_ready events
    async handleNotificationReady(payload) {
      const {galleryId, notificationData} = payload

      // Only handle notifications for this gallery
      if (galleryId != this.gallery.id) {
        return
      }

      // Default behavior: check for new media and request popup via event bus
      try {
        const checkResult = await this.checkNewMedia({
          galleryId: this.gallery.id,
          currentData: this.gallery,
          fullRefresh: notificationData.type === 'picacode_update'
        })

        if (checkResult && checkResult.hasNewMedia) {
          // Emit event to show popup - App.vue will handle it
          await this.emitEvent(eventBusConstants.realtimeNotifications.show_popup, {
            galleryId: galleryId,
            galleryName: notificationData.galleryName,
            checkResult: checkResult,
            onRefresh: async () => {
              // 1. Refresh the gallery tree data
              await this.fetchGalleryTree({checkinId: this.checkinId, galleryId: this.rootGalleryId})

              // 2. Reset to page 1 and refresh media
              await this.loadMedia(1, false)
            }
          })
        }

      } catch (error) {
        console.error('Error handling notification_ready:', error)
      }
    }

  },

  async mounted() {
    // Set initial active tab to first tab with count > 0.
    // changeTab already loads the media for the selected tab, so we only
    // fall back to a plain loadMedia() when no valid tab exists (non-tabbed
    // display types).
    let handledByTabChange = false
    if (this.gallery && this.gallery.tabs) {
      const firstValidTab = this.gallery.tabs.find(tab => tab.count > 0)
      if (firstValidTab) {
        await this.changeTab(firstValidTab.type)
        handledByTabChange = true
      }
    }

    if (!handledByTabChange) {
      await this.loadMedia()
    }

    // Setup MQTT subscriptions
    this.setupMqttSubscriptions()

    // Listen for notification_ready events
    this.registerEvent(eventBusConstants.realtimeNotifications.notification_ready, this.handleNotificationReady)
  },

  beforeDestroy() {
    // Cleanup MQTT subscriptions
    this.cleanupMqttSubscriptions()

    // Unregister event listeners
    this.unregisterEvent(eventBusConstants.realtimeNotifications.notification_ready, this.handleNotificationReady)
  }
}
</script>